import { IComponentOptions, IController, IScope } from "angular";
import { IModalService } from "angular-ui-bootstrap";
import { StateService, TransitionService } from "@uirouter/angularjs";
import { take } from "rxjs";
import { AccountService } from "@gtmhub/accounts/accounts-service";
import { generateDefaultMandatoryOkrAlignmentAccountSettings } from "@gtmhub/configuration/utils/utils";
import { IEmployeeDetails } from "@gtmhub/employees";
import { UIErrorHandlingService } from "@gtmhub/error-handling";
import { IGoal } from "@gtmhub/goals/models";
import { IHomeMetric } from "@gtmhub/home/home.models";
import { GoalsChangedEventArgs, IMetricChangedEventArgs, OkrEvents } from "@gtmhub/okrs/events";
import { IMetricFormParams } from "@gtmhub/okrs/metrics/controllers/metric-form";
import { PlanningSessionsActions } from "@gtmhub/sessions/redux/session-actions";
import { ISessionsStoreState } from "@gtmhub/sessions/redux/session-reducer";
import { DefaultSessionResolver } from "@gtmhub/sessions/services/default-session-resolver";
import { Ng1UiModalService } from "@gtmhub/shared/components/modal/ng1-modal.service";
import { INgRedux } from "@gtmhub/state-management";
import { ITeam } from "@gtmhub/teams";
import { getCurrentUserId } from "@gtmhub/users";
import { UserInvitationModalService } from "@gtmhub/users/components/user-invitation-form/user-invitation-modal.service";
import { IdMap, toIdMap } from "@gtmhub/util";
import { AnalyticsService } from "@webapp/analytics/services/analytics.service";
import { IMandatoryOkrAlignmentSettings } from "@webapp/configuration/models/configuration.model";
import { FeatureFlag } from "@webapp/feature-toggles/models/feature-toggles.models";
import { FeatureTogglesFacade } from "@webapp/feature-toggles/services/feature-toggles-facade.service";
import { GuidedOKRsComponent, GuidedOKRsModalData } from "@webapp/platform-intelligence/guided-okrs/guided-okrs.component";
import { PiFeatureVisibilityFacade } from "@webapp/platform-intelligence/shared/services/pi-feature-visibility-facade.service";
import { PiTrackingFlowNameEnum } from "@webapp/platform-intelligence/shared/utils/pi-tracking";
import { TodoCheckIns } from "@webapp/reflections/models/reflections.models";
import { Session } from "@webapp/sessions/models/sessions.model";
import { Task } from "@webapp/tasks/models/tasks.models";
import { CreateYourFirstWhiteboardComponent } from "@webapp/whiteboards/components/create-your-first-whiteboard/create-your-first-whiteboard.component";
import { CreateFirstWhiteboardType } from "@webapp/whiteboards/components/create-your-first-whiteboard/models";
import { HowToStartModalOpenerService } from "../../shared/services/how-to-start-modal-opener.service";
import { IChecklist } from "../models/onboarding.models";
import { OnboardingService } from "../onboarding.service";

const bindStateToScope = (state: ISessionsStoreState) => {
  return {
    sessions: state.sessions.items,
  };
};
export interface OnboardingHomeComponentCtrl extends IController, ReturnType<typeof bindStateToScope> {}
export class OnboardingHomeComponentCtrl {
  private okrEvents: OkrEvents;

  isGuidedOKRsCreationEnabled: boolean;
  isOkrAlignmentMandatory: boolean;
  activeSessions: Session[];
  activeOpenSessions: Session[];
  employee: IEmployeeDetails;
  todoItems: {
    tasks: Task[];
    metrics: IHomeMetric[];
    goalsToApprove: IGoal[];
  };
  todoReflections: TodoCheckIns[];
  myOkrs: IGoal[];
  teams: ITeam[];

  activeSessionIds: string[];
  activeSessionsMap: IdMap<Session>;
  myTeamsOkrsCollapsed: IdMap<boolean>;
  displayOnboardingCard: {
    learnTheBasics: boolean;
    inviteTeammate: boolean;
    visitDemoAccount: boolean;
    visitMarketplace: boolean;
  };
  completeOnboardingCard: {
    inviteTeammate: boolean;
    visitMarketplace: boolean;
    visitDemoAccount: boolean;
  };
  checklist: IChecklist;
  showKrLabel: boolean;

  static $inject = [
    "$scope",
    "$transitions",
    "$state",
    "OnboardingService",
    "UIErrorHandlingService",
    "$uibModal",
    "AnalyticsService",
    "HowToStartModalOpenerService",
    "UserInvitationModalService",
    "AccountService",
    "Ng1UiModalService",
    "DefaultSessionResolver",
    "PlanningSessionsActions",
    "FeatureTogglesFacade",
    "PiFeatureVisibilityFacade",
    "$ngRedux",
  ];

  constructor(
    private $scope: IScope,
    private $transitions: TransitionService,
    private $state: StateService,
    private onboardingService: OnboardingService,
    private uiErrorHandlingService: UIErrorHandlingService,
    private $uibModal: IModalService,
    private analyticsService: AnalyticsService,
    private howToStartModalOpenerService: HowToStartModalOpenerService,
    private userInvitationModal: UserInvitationModalService,
    private accountService: AccountService,
    private ng1UiModalService: Ng1UiModalService,
    private defaultSessionResolver: DefaultSessionResolver,
    private planningSessionsActions: PlanningSessionsActions,
    private featureToggleService: FeatureTogglesFacade,
    private piFeatureVisibilityFacade: PiFeatureVisibilityFacade,
    $ngRedux: INgRedux
  ) {
    this.okrEvents = new OkrEvents(this.$scope);

    this.planningSessionsActions.getSessionsIfMissing();

    const unsubscribe = $ngRedux.connect(bindStateToScope)(this);
    this.$scope.$on("$destroy", unsubscribe);
  }

  $onInit(): void {
    this.$scope.$on("onboarding.createdDemoAccount", () => this.completeVisitDemoAccount());

    this.$scope.$on(
      "$destroy",
      this.$transitions.onSuccess({}, () => {
        this.checkToShowKrLabel();
      })
    );

    this.okrEvents.onGoalsChanged((args) => this.onGoalsChanged(args));

    this.activeSessionIds = this.activeSessions.map((session) => session.id);
    this.activeSessionsMap = toIdMap(this.activeSessions);
    this.checklist = this.onboardingService.initChecklist();
    const { showCards, stepsV1 } = this.checklist;

    this.displayOnboardingCard = {
      learnTheBasics: !this.myOkrs?.length,
      inviteTeammate: showCards.inviteTeammate,
      visitDemoAccount: showCards.visitDemoAccount,
      visitMarketplace: this.accountService.featureAvailable("marketplace") && showCards.visitMarketplace,
    };

    this.completeOnboardingCard = {
      inviteTeammate: stepsV1.invitedTeammate,
      visitDemoAccount: stepsV1.visitedDemoAccount,
      visitMarketplace: this.accountService.featureAvailable("marketplace") && stepsV1.visitedMarketplace,
    };

    this.initMyTeamsOkrsCollapsed();
    this.checkToShowKrLabel();
    const mandatoryOkrAlignmentSettings: IMandatoryOkrAlignmentSettings =
      this.accountService.getAccountSetting("mandatoryOkrAlignment") || generateDefaultMandatoryOkrAlignmentAccountSettings();
    this.featureToggleService.isFeatureAvailable(FeatureFlag.MandatoryOkrAlignmentFeature).then((isMandatoryOkrAlignmentEnabled) => {
      this.isOkrAlignmentMandatory =
        isMandatoryOkrAlignmentEnabled && mandatoryOkrAlignmentSettings.enabled && !mandatoryOkrAlignmentSettings.excludedUserIds.includes(getCurrentUserId());
    });

    this.piFeatureVisibilityFacade
      .isGuidedOKRsCreationVisible$()
      .pipe(take(1))
      .subscribe((enabled) => (this.isGuidedOKRsCreationEnabled = enabled));
  }

  openVideoModal(): void {
    this.checklist = this.onboardingService.openVideoModal(this.checklist);
    this.$scope.$root.$broadcast("onboarding.checklistUpdated", this.checklist);
  }

  openWizard = (): void => {
    const defaultSession = this.defaultSessionResolver.getDefaultSession(this.sessions);
    this.ng1UiModalService.create<GuidedOKRsComponent, GuidedOKRsModalData>({
      uiContent: GuidedOKRsComponent,
      uiFooter: null,
      uiClosable: false,
      uiKeyboard: false,
      uiWrapClassName: "guided-okrs-modal-wrapper",
      uiData: {
        currentSession: defaultSession,
        flowName: PiTrackingFlowNameEnum.GuidedOkrsCreationTrialHomeEmptyState,
      },
    });
  };

  openSelectHowToStartModal(): void {
    if (this.isOkrAlignmentMandatory) {
      this.$state.go("gtmhub.home.dashboard._goals._goalForm.form");
    } else if (this.isGuidedOKRsCreationEnabled) {
      this.ng1UiModalService
        .create<CreateYourFirstWhiteboardComponent, CreateFirstWhiteboardType>({
          uiContent: CreateYourFirstWhiteboardComponent,
          uiFooter: null,
        })
        .afterClose.subscribe((type) => {
          if (type === "suggestion") {
            this.openWizard();
            return;
          }
          if (type === "template") {
            this.analyticsService.track("Create First OKR Clicked", { how_to_start: "with a template" });
            this.openGoalCreationModal({ shouldStartWithTemplate: true });
            return;
          }

          this.analyticsService.track("Create First OKR Clicked", { how_to_start: "on your own" });
          this.openGoalCreationModal({ shouldStartWithTemplate: false });
        });
    } else {
      this.howToStartModalOpenerService.openHowToStartModal([
        {
          title: "start_with_template",
          text: "we_have_crafted_hundreds_templates",
          img: require("wwwroot/img/screens/onboarding-home/visit-marketplace-inverted.svg"),
          button: {
            actionCb: () => {
              this.analyticsService.track("Create First OKR Clicked", { how_to_start: "with a template" });
              this.openGoalCreationModal({ shouldStartWithTemplate: true });
            },
            kind: "primary",
          },
        },
        {
          title: "start_on_your_own",
          text: "if_you_know_how_to_create",
          img: require("wwwroot/img/screens/onboarding-home/live-demo.svg"),
          button: {
            actionCb: () => {
              this.analyticsService.track("Create First OKR Clicked", { how_to_start: "on your own" });
              this.openGoalCreationModal({ shouldStartWithTemplate: false });
            },
            kind: "secondary",
          },
        },
      ]);
    }
  }

  openGoalCreationModal(params: { shouldStartWithTemplate: boolean }): void {
    // We don't care which is the first session
    const externalSessionId = this.activeOpenSessions[0]?.id;
    const isOnlyOpenSession = this.activeOpenSessions.length === 1;

    this.$uibModal.open({
      windowClass: `create-first-goal ${params.shouldStartWithTemplate ? "wide" : "small"}`,
      backdropClass: "dark-backdrop",
      component: "createFirstGoalForm",
      lazyLoadState: "gtmhub.goals",
      resolve: {
        externalSessionId: () => externalSessionId,
        isOnlyOpenSession: () => isOnlyOpenSession,
        shouldStartWithTemplate: () => params.shouldStartWithTemplate,
      },
    });
  }

  createKr(): void {
    // This method is visible only if there is one Goal
    const metricFormParams: Partial<IMetricFormParams> = {
      id: this.myOkrs[0].id,
      flowName: PiTrackingFlowNameEnum.GuidedOkrsCreationTrialHomeEmptyState,
    };

    this.$state.go("gtmhub.home.dashboard._createMetric.createMetric", metricFormParams);
  }

  visitMarketplace(): void {
    this.checklist = this.onboardingService.visitMarketplace(this.checklist);
    this.completeOnboardingCard.visitMarketplace = true;
    this.$scope.$root.$broadcast("onboarding.checklistUpdated", this.checklist);
  }

  createDemoAccount(): void {
    this.onboardingService.createDemoAccount(this.checklist).then(
      (result) => {
        this.checklist = result;
        this.completeVisitDemoAccount();
        this.$scope.$root.$broadcast("onboarding.checklistUpdated", this.checklist);
      },
      (error) => {
        this.uiErrorHandlingService.handleModal(error);
      }
    );
  }

  removeOnboardingCard(onboardingCard: keyof OnboardingHomeComponentCtrl["displayOnboardingCard"]): void {
    this.displayOnboardingCard[onboardingCard] = false;
    this.checklist.showCards[onboardingCard] = false;
    this.$scope.$root.$broadcast("onboarding.checklistUpdated", this.checklist);
  }

  openInvitationModal(): void {
    this.userInvitationModal.open();
  }

  private initMyTeamsOkrsCollapsed(): void {
    this.myTeamsOkrsCollapsed = this.teams.reduce((idMap, team) => ((idMap[team.id] = true), idMap), {} as IdMap<boolean>);
  }

  private completeVisitDemoAccount(): void {
    this.completeOnboardingCard.visitDemoAccount = true;
  }

  private onGoalsChanged(args: GoalsChangedEventArgs): void {
    const checkMetricActions = this.myOkrs.length === 1 && (args as IMetricChangedEventArgs).detail.goalId === this.myOkrs[0].id;

    switch (args.reason) {
      case "goalCreated":
        this.displayOnboardingCard.learnTheBasics = false;

        if (!this.myOkrs.length) {
          // We want to push only the first goal and home-my-okrs will be inited where is the logic for the OKRs
          this.myOkrs.push(args.detail.goal);
        }
        break;

      case "metricCreated":
        if (checkMetricActions) {
          if (this.myOkrs[0].metrics) {
            this.myOkrs[0].metrics.push(args.detail.metric);
          } else {
            this.myOkrs[0].metrics = [args.detail.metric];
          }
        }
        break;

      case "metricDeleted":
        if (checkMetricActions) {
          this.myOkrs[0].metrics = this.myOkrs[0].metrics.filter((metric) => metric.id !== args.detail.metricId);
        }
        break;
    }

    this.checkToShowKrLabel();
  }

  private checkToShowKrLabel(): void {
    const isOnlyOneOKR = this.myOkrs?.length === 1;
    const goalHasNoMetrics = !this.myOkrs[0]?.metrics?.length;
    const isNotGoalState = !this.$state.current.name.includes("._goalSidebar.goal");

    this.showKrLabel = isOnlyOneOKR && goalHasNoMetrics && isNotGoalState;
  }
}

export const OnboardingHomeComponent: IComponentOptions = {
  controller: OnboardingHomeComponentCtrl,
  template: require("./onboarding-home.html"),
  bindings: {
    activeSessions: "<",
    activeOpenSessions: "<",
    employee: "<",
    todoItems: "<",
    todoReflections: "<",
    myOkrs: "<",
    teams: "<",
  },
};
